import React from 'react';
import { endOfDay } from 'date-fns';
import { Typography } from '@mui/material';
import {
    CapacityType,
    JobType,
    OpenCapacityType,
    OrderForCapacitySuggestionType
} from '../types';
import { formatDate, formatDateTime, getTimeUntil } from './parsing';

type OrderForDateType = {
    firstPickupDate: string,
    lastPickupDate: string | null,
    firstDeliveryDate: string,
    lastDeliveryDate: string | null,
    firstPickupTime: string | null,
    lastPickupTime: string | null,
    firstDeliveryTime: string | null,
    lastDeliveryTime: string | null,
    expiresAt: string | null
};

export function formatOrderPublishedFor(entity: {
    isPublic?: boolean;
    groups?: { name: string }[] | null;
    recipients?: { name?: string }[] | null;
    recipientCount?: number | null;
}) {
    if (entity.isPublic) {
        return 'Alla';
    }
    const parts: string[] = [];
    if (entity.groups?.length) {
        parts.push(...entity.groups.map((g) => g.name));
    }
    const recipients = entity.recipients || [];
    if (recipients.length > 0) {
        const names = recipients.map((r) => r.name).filter(Boolean) as string[];
        if (names.length) {
            parts.push(...names);
        } else {
            parts.push(recipients.length === 1 ? '1 valt åkeri' : `${recipients.length} valda åkerier`);
        }
    } else if ((entity.recipientCount || 0) > 0) {
        parts.push(entity.recipientCount === 1 ? '1 valt åkeri' : `${entity.recipientCount} valda åkerier`);
    }
    return parts.length ? parts.join(', ') : 'Valda åkerier';
}

export function getOrderDate(order: OrderForDateType) {
    const earliestDate = order.firstPickupDate;
    const latestDate = order.lastDeliveryDate || order.firstDeliveryDate;

    return earliestDate.slice(0, 10) === latestDate.slice(0, 10)
        ? formatDate(earliestDate)
        : `${formatDate(earliestDate)} - ${formatDate(latestDate)}`;
}

export type OrderTimeLeftType = {
    firstPickupDate: string,
    lastPickupDate: string | null,
    expiresAt?: string | null
}

export function getOrderExpiryDate({ firstPickupDate, lastPickupDate, expiresAt }: OrderTimeLeftType) {
    return expiresAt ? new Date(expiresAt) : endOfDay(new Date(lastPickupDate || firstPickupDate));
}

export function getFormattedOrderExpiryDate(order: OrderTimeLeftType) {
    return formatDateTime(getOrderExpiryDate(order));
}

export function getOrderTimeLeft(order: OrderTimeLeftType) {
    return getTimeUntil(getOrderExpiryDate(order));
}

export function getPublishedText({
    entity,
    handleNavigateToCompanyProfile
} : {
    entity: OpenCapacityType | JobType | null,
    handleNavigateToCompanyProfile?: () => void
}) {
    if (!entity) {
        return null;
    }
    if (entity.type === 'DHL') {
        return (
            <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                {`Hämtat ${formatDateTime(entity.publishedAt)} från DHL Overflow.`}
            </Typography>
        );
    }
    if (entity.type === 'LL') {
        const canOpenProfile = Boolean(
            entity.tenantId && !entity.isRejected && entity.isCarrier && handleNavigateToCompanyProfile
        );
        return (
            <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>

                {`Publicerat ${formatDateTime(entity.publishedAt)} av `}
                <Typography
                    component="span"
                    variant="caption"
                    sx={{
                        color: canOpenProfile ? 'primary.main' : 'inherit',
                        cursor: canOpenProfile ? 'pointer' : 'default',
                        textDecoration: canOpenProfile ? 'underline' : 'none',
                        '&:hover': canOpenProfile ? { textDecoration: 'underline', opacity: 0.8 } : {}
                    }}
                    onClick={
                        canOpenProfile
                            ? () => handleNavigateToCompanyProfile?.()
                            : undefined
                    }
                >
                    {entity.tenantName || '[dold uppdragsgivare]'}
                </Typography>
                {` för ${formatOrderPublishedFor(entity)}.`}
            </Typography>
        );
    }
    if (entity.type === 'CAP') {
        return (
            <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                {`Publicerad ${formatDateTime(entity.publishedAt)
                } för ${entity.isPublic ? 'Alla' : entity.groups.map((g) => g.name).join(', ')}.`}
            </Typography>
        );
    }
    return null;
}

type GetFillFactorPropsType = {
    capacity: OpenCapacityType | CapacityType,
    order: OrderForCapacitySuggestionType
};

export function getFillFactorPercentage({ capacity, order }: GetFillFactorPropsType) {
    const attributes = [
        'grossWeight' as 'grossWeight',
        'loadingMeters' as 'loadingMeters',
        'volume' as 'volume',
        'palletPlaces' as 'palletPlaces',
        'pallets' as 'pallets'
    ];

    return attributes.reduce((acc, curr) => (
        // @ts-ignore
        order[curr] && capacity[curr] && (order[curr] / capacity[curr] > acc)
            // @ts-ignore
            ? (order[curr] / capacity[curr])
            : acc
    ), 0) * 100;
}
